import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const SIZE_BRACKET_REGEX = /^(.+?)\s*\[([^\]]+)\]$/;

const SUMMARY_CURRENCY_SYMBOL = { USD: '$', KRW: '₩', IDR: 'Rp' };

export const ISSUANCE_PAGE_LIMIT = 50;

/** 서버에 보낼 숫자 ID — 비숫자·빈 값은 null */
export const toNumericId = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/** 로드된 행만으로 summary 계산 (서버 summary 실패 시 fallback) */
export const computeInvoiceSummary = (rows, language = 'en') => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const amountByCurrency = {};
    let totalQtySum = 0;
    let validRowCount = 0;
    rows.forEach((row) => {
        const q = Number(row?.gi_request_line_qty);
        if (!Number.isFinite(q) || q <= 0) return;
        validRowCount += 1;
        totalQtySum += q;
        const unit = Number(row?.price);
        if (!Number.isFinite(unit)) return;
        const currency = row?.price_currency || 'UNKNOWN';
        amountByCurrency[currency] = (amountByCurrency[currency] || 0) + unit * q;
    });
    const langKey = String(language || 'en').split('-')[0];
    const locale = langKey === 'ko' ? 'ko-KR' : langKey === 'id' ? 'id-ID' : 'en-US';
    const amountFormatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    const amounts = Object.entries(amountByCurrency).map(([cur, amt]) => ({
        currency: cur,
        symbol: SUMMARY_CURRENCY_SYMBOL[cur] ?? cur,
        formatted: amountFormatter.format(amt)
    }));
    return {
        count: validRowCount,
        totalQty: formatInvoiceInteger(totalQtySum, language) ?? '0',
        amounts
    };
};

/** 타임존에 따라 ms가 0이 아닐 수 있어, 달력상 1970-01-01은 빈 납품일로 취급 */
const isUnixEpochCalendarDate = (d) =>
    d && d.year() === 1970 && d.month() === 0 && d.date() === 1;

/** 납품일이 없을 때 0·Unix epoch 등이 오면 DatePicker에 1970.1.1로 보이므로 비움(null). */
export const parseDeliveryDateForUi = (raw) => {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number') {
        if (raw === 0 || !Number.isFinite(raw)) return null;
    }
    if (typeof raw === 'string') {
        const t = raw.trim();
        if (t === '' || t === '0') return null;
        if (/^1970[.\-/]01[.\-/]01(\b|T|$)/i.test(t)) return null;
    }
    if (raw && typeof raw.isValid === 'function' && typeof raw.valueOf === 'function') {
        if (!raw.isValid()) return null;
        if (raw.valueOf() === 0) return null;
        if (isUnixEpochCalendarDate(raw)) return null;
        return raw;
    }
    const d = dayjs(raw);
    if (!d.isValid()) return null;
    if (d.valueOf() === 0) return null;
    if (isUnixEpochCalendarDate(d)) return null;
    return d;
};

const invoiceNumberLocale = (i18nLanguage = 'en') => {
    const langKey = String(i18nLanguage || 'en').split('-')[0];
    return langKey === 'ko' ? 'ko-KR' : langKey === 'id' ? 'id-ID' : 'en-US';
};

/** ORDER QTY 등 정수 수량: 언어별 천 단위 구분 */
export const formatInvoiceInteger = (value, i18nLanguage = 'en') => {
    if (value == null || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return new Intl.NumberFormat(invoiceNumberLocale(i18nLanguage), {
        maximumFractionDigits: 0
    }).format(Math.trunc(n));
};

/** 서버 summary raw → TableControls용 포맷 (computeInvoiceSummary와 동일 shape) */
export const mapIssuanceSummaryToUi = (payload, language = 'en') => {
    if (!payload || typeof payload !== 'object') return null;
    const langKey = String(language || 'en').split('-')[0];
    const locale = langKey === 'ko' ? 'ko-KR' : langKey === 'id' ? 'id-ID' : 'en-US';
    const amountFormatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    const amounts = Array.isArray(payload.amounts)
        ? payload.amounts.map((a) => {
            const currency = a?.currency || 'UNKNOWN';
            const amt = Number(a?.amount);
            return {
                currency,
                symbol: SUMMARY_CURRENCY_SYMBOL[currency] ?? currency,
                formatted: amountFormatter.format(Number.isFinite(amt) ? amt : 0)
            };
        })
        : [];
    const totalCount = Number(payload.total_count);
    const totalQty = Number(payload.total_qty);
    return {
        count: Number.isFinite(totalCount) ? totalCount : 0,
        totalQty: formatInvoiceInteger(Number.isFinite(totalQty) ? totalQty : 0, language) ?? '0',
        amounts
    };
};

/**
 * GI REQ 컬럼용: 앱 언어에 맞는 날짜 형식 + `날짜 (수량)` (예: en 2026-03-03 (1,000), ko 2026.03.03 (1,000))
 */
export const formatGiReqPlannedDisplay = (plannedAtRaw, qtyRaw, i18nLanguage = 'en') => {
    const langKey = String(i18nLanguage || 'en').split('-')[0];
    const dateFormat =
        langKey === 'ko' ? 'YYYY.MM.DD' : langKey === 'id' ? 'DD/MM/YYYY' : 'YYYY-MM-DD';

    const parsed = parseDeliveryDateForUi(plannedAtRaw);
    const dateStr =
        parsed && typeof parsed.format === 'function' ? parsed.format(dateFormat) : null;

    let qtyStr = null;
    if (qtyRaw != null && qtyRaw !== '') {
        qtyStr = formatInvoiceInteger(qtyRaw, i18nLanguage);
    }

    if (dateStr && qtyStr != null) {
        return `${dateStr} (${qtyStr})`;
    }
    if (dateStr) {
        return dateStr;
    }
    if (qtyStr != null) {
        return qtyStr;
    }
    return '-';
};

/** Buyer 컬럼: 마스터 바이어 명과 API `buyer_detail`을 함께 표시 */
export const formatBuyerColumnDisplay = (buyerName, buyerDetail) => {
    const name = buyerName != null ? String(buyerName).trim() : '';
    const detail = buyerDetail != null ? String(buyerDetail).trim() : '';
    // 오더 현황 페이지와 동일한 표시 형식: "buyer_name (buyer_detail)"
    if (name && detail) return `${name} (${detail})`;
    if (name) return name;
    if (detail) return detail;
    return '';
};

/** invoice_lines 항목: INVOICE NO 컬럼 수량 — API `quantity` 우선, 없으면 `qty` */
export const getInvoiceLineDisplayQuantity = (ln) => ln?.quantity ?? ln?.qty ?? null;

/** INVOICE NO 셀: 번호 + 수량 (예: INV-2024-001 (1,000)) */
export const formatInvoiceNoQtyLine = (invoiceNo, qty, i18nLanguage = 'en') => {
    const no =
        invoiceNo != null && String(invoiceNo).trim() !== '' ? String(invoiceNo).trim() : '';
    const qtyStr = formatInvoiceInteger(qty, i18nLanguage);
    if (no && qtyStr != null) return `${no} (${qtyStr})`;
    if (no) return no;
    if (qtyStr != null) return `(${qtyStr})`;
    return '-';
};

/**
 * list_with_items_by_sub_order_id_list 응답 → sub_order_id 문자열 키별 invoice 라인 목록
 */
export const buildInvoiceLinesBySubOrderId = (apiList) => {
    const map = {};
    if (!Array.isArray(apiList)) return map;
    apiList.forEach((inv) => {
        if (!inv || typeof inv !== 'object') return;
        const headerInvoiceNo =
            inv.invoice_no ?? inv.invoiceNo ?? inv.invoice_number ?? inv.invoiceNumber ?? '';
        const invIdRaw = inv.invoice_id ?? inv.invoiceId ?? inv.id ?? null;
        const invoiceId =
            invIdRaw != null && String(invIdRaw).trim() !== '' && Number.isFinite(Number(invIdRaw))
                ? Number(invIdRaw)
                : null;
        const headerInvoiceDate =
            inv.created_at ?? inv.createdAt ?? inv.invoice_date ?? inv.invoiceDate ?? null;
        const rawItems =
            inv.items ??
            inv.invoice_items ??
            inv.invoiceItems ??
            inv.invoice_lines ??
            inv.invoiceLines ??
            inv.invoice_item_list ??
            inv.invoiceItemList ??
            inv.lines ??
            inv.line_items ??
            inv.lineItems ??
            [];

        const pushLine = ({ subOrderId, invoiceNo, qty, lineInvoiceId, giRequestLineId, giRequestId, invoiceDate }) => {
            if (subOrderId == null || subOrderId === '') return;
            const key = String(subOrderId);
            if (!map[key]) map[key] = [];
            const quantity = qty ?? null;
            map[key].push({
                invoice_id: lineInvoiceId ?? invoiceId,
                invoice_no: invoiceNo != null ? String(invoiceNo) : '',
                quantity,
                qty: quantity,
                gi_request_line_id: giRequestLineId ?? null,
                gi_request_id: giRequestId ?? null,
                invoice_date: invoiceDate ?? null
            });
        };

        // 1) "그룹(헤더 + items)" 형태
        if (Array.isArray(rawItems) && rawItems.length > 0) {
            rawItems.forEach((it) => {
                if (!it || typeof it !== 'object') return;
                const invoiceNo =
                    it.invoice_no ??
                    it.invoiceNo ??
                    it.invoice_number ??
                    it.invoiceNumber ??
                    headerInvoiceNo ??
                    '';
                const qty = it.quantity ?? it.qty ?? null;

                const lineInvIdRaw = it.invoice_id ?? it.invoiceId ?? invIdRaw;
                const lineInvoiceId =
                    lineInvIdRaw != null &&
                    String(lineInvIdRaw).trim() !== '' &&
                    Number.isFinite(Number(lineInvIdRaw))
                        ? Number(lineInvIdRaw)
                        : invoiceId;

                let giRequestLineId = it.gi_request_line_id ?? it.giRequestLineId ?? null;
                let giRequestId = it.gi_request_id ?? it.giRequestId ?? null;

                const remarkStr = typeof it.remark === 'string' ? it.remark : '';
                if (remarkStr) {
                    if (giRequestLineId == null || giRequestLineId === '') {
                        const m = remarkStr.match(/GI_LINE:(\d+)/);
                        if (m) giRequestLineId = Number(m[1]);
                    }
                    if (giRequestId == null || giRequestId === '') {
                        const m = remarkStr.match(/GI_REQ:(\d+)/);
                        if (m) giRequestId = Number(m[1]);
                    }
                }

                const itemInvoiceDate =
                    it.created_at ?? it.createdAt ?? it.invoice_date ?? it.invoiceDate ?? headerInvoiceDate;

                const soId = it.sub_order_id ?? it.subOrderId;
                const soIdList = it.sub_order_id_list ?? it.subOrderIdList ?? it.sub_order_ids;
                if (Array.isArray(soIdList) && soIdList.length > 0) {
                    soIdList.forEach((sid) =>
                        pushLine({ subOrderId: sid, invoiceNo, qty, lineInvoiceId, giRequestLineId, giRequestId, invoiceDate: itemInvoiceDate })
                    );
                    return;
                }
                pushLine({ subOrderId: soId, invoiceNo, qty, lineInvoiceId, giRequestLineId, giRequestId, invoiceDate: itemInvoiceDate });
            });
            return;
        }

        // 2) "플랫 라인" 형태: inv 자체가 line처럼 내려오는 경우 (sub_order_id + invoice_no가 직접 존재)
        const flatSoId = inv.sub_order_id ?? inv.subOrderId;
        const flatQty = inv.quantity ?? inv.qty ?? null;
        const flatInvoiceNo = headerInvoiceNo ?? '';
        let flatGiRequestLineId = inv.gi_request_line_id ?? inv.giRequestLineId ?? null;
        let flatGiRequestId = inv.gi_request_id ?? inv.giRequestId ?? null;
        const flatRemark = typeof inv.remark === 'string' ? inv.remark : '';
        if (flatRemark) {
            if (flatGiRequestLineId == null || flatGiRequestLineId === '') {
                const m = flatRemark.match(/GI_LINE:(\d+)/);
                if (m) flatGiRequestLineId = Number(m[1]);
            }
            if (flatGiRequestId == null || flatGiRequestId === '') {
                const m = flatRemark.match(/GI_REQ:(\d+)/);
                if (m) flatGiRequestId = Number(m[1]);
            }
        }
        if (flatSoId != null && flatSoId !== '' && (flatInvoiceNo || flatQty != null)) {
            pushLine({
                invoiceDate: inv.created_at ?? inv.createdAt ?? inv.invoice_date ?? inv.invoiceDate ?? headerInvoiceDate,
                subOrderId: flatSoId,
                invoiceNo: flatInvoiceNo,
                qty: flatQty,
                lineInvoiceId: invoiceId,
                giRequestLineId: flatGiRequestLineId,
                giRequestId: flatGiRequestId
            });
        }
    });
    return map;
};

const addInvoiceLineQty = (a, b) => {
    const na = a != null && a !== '' ? Number(a) : NaN;
    const nb = b != null && b !== '' ? Number(b) : NaN;
    if (Number.isFinite(na) && Number.isFinite(nb)) return na + nb;
    if (Number.isFinite(na)) return na;
    if (Number.isFinite(nb)) return nb;
    return a ?? b ?? null;
};

/**
 * INVOICE NO 컬럼 표시용: 동일 번호는 한 번만 보이게 하고 수량은 합산.
 * 번호가 비어 있으면 invoice_id로 구분, 둘 다 없으면 행별로 유지.
 */
export const dedupeInvoiceLinesByInvoiceNo = (lines) => {
    if (!Array.isArray(lines) || lines.length === 0) return [];
    if (lines.length === 1) return [...lines];
    const map = new Map();
    let fallbackIdx = 0;
    for (const ln of lines) {
        if (!ln || typeof ln !== 'object') continue;
        const no = `${ln.invoice_no ?? ''}`.trim();
        let key;
        if (no !== '') {
            key = `no:${no}`;
        } else if (ln.invoice_id != null && `${ln.invoice_id}`.trim() !== '') {
            key = `id:${ln.invoice_id}`;
        } else {
            key = `_:${fallbackIdx}`;
            fallbackIdx += 1;
        }
        const existing = map.get(key);
        if (!existing) {
            map.set(key, { ...ln });
        } else {
            const merged = addInvoiceLineQty(
                getInvoiceLineDisplayQuantity(existing),
                getInvoiceLineDisplayQuantity(ln)
            );
            existing.quantity = merged;
            existing.qty = merged;
        }
    }
    return [...map.values()];
};

/**
 * 동일 sub_order 등에서 INVOICE NO를 위→아래로 일관되게: 번호 오름차순(숫자 인식), 동일 시 invoice_id.
 */
export const sortInvoiceLinesForDisplay = (lines) => {
    if (!Array.isArray(lines) || lines.length <= 1) return lines ? [...lines] : [];
    return [...lines].sort((a, b) => {
        const na = `${a?.invoice_no ?? ''}`.trim();
        const nb = `${b?.invoice_no ?? ''}`.trim();
        const cmp = na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
        if (cmp !== 0) return cmp;
        const ida = Number(a?.invoice_id);
        const idb = Number(b?.invoice_id);
        if (Number.isFinite(ida) && Number.isFinite(idb) && ida !== idb) return ida - idb;
        return 0;
    });
};

/** INVOICE NO 컬럼: 중복 제거 후 표시 순서 정렬 */
export const getDedupedSortedInvoiceLines = (lines) =>
    sortInvoiceLinesForDisplay(dedupeInvoiceLinesByInvoiceNo(lines));

/** INVOICE NO가 표시되는 행인지 (필터용): 직접 필드 또는 invoice_lines의 번호 */
export const rowHasInvoiceNo = (row) => {
    if (!row || typeof row !== 'object') return false;
    const direct = `${row.invoice_no ?? row.existing_invoice_no ?? ''}`.trim();
    if (direct) return true;
    const lines = row.invoice_lines;
    if (!Array.isArray(lines)) return false;
    return lines.some((ln) => `${ln?.invoice_no ?? ''}`.trim() !== '');
};

/**
 * "미발급만" 토글 ON일 때 행을 남길지: 인보이스가 없거나,
 * order_qty보다 INVOICE NO(중복 제거·정렬 후 라인) 발급 수량 합이 작으면(부분 발급) 항상 표시.
 * order_qty를 알 수 없으면 기존처럼 인보이스만 있으면 숨김 대상.
 */
export const keepRowForUnissuedOnlyView = (row) => {
    if (!rowHasInvoiceNo(row)) return true;
    const oq = Number(row?.order_qty);
    if (!Number.isFinite(oq) || oq <= 0) return false;
    let invoicedSum = 0;
    for (const ln of getDedupedSortedInvoiceLines(row.invoice_lines || [])) {
        const q = getInvoiceLineDisplayQuantity(ln);
        const n = q != null && q !== '' ? Number(q) : NaN;
        if (Number.isFinite(n)) invoicedSum += n;
    }
    return invoicedSum < oq;
};

/**
 * 테이블 상단 토글(내 오더만 / 미발급만)과 동일한 필터 — 후보 행 배열에 적용
 */
export const applyInvoiceIssuanceDisplayFilters = (
    rows,
    { showMyOrderOnly, hideInvoicedRows, userId } = {}
) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    let r = rows;
    if (showMyOrderOnly && userId) {
        const loginId = String(userId).toLowerCase();
        r = r.filter((row) => (row.created_by || '').toString().toLowerCase() === loginId);
    }
    if (hideInvoicedRows) {
        r = r.filter((row) => keepRowForUnissuedOnlyView(row));
    }
    return r;
};

const giReqQtyForInvoiceSort = (row) => Number(row.gi_request_line_qty) || 0;

const priceSumForInvoiceSort = (row) => {
    if (row.price == null || row.price === '') return 0;
    const unit = Number(row.price);
    if (!Number.isFinite(unit)) return 0;
    return giReqQtyForInvoiceSort(row) * unit;
};

/** InvoiceTable 정렬 키와 동일한 비교값 (데스크톱 테이블과 목록 slice 일치용) */
export const getInvoiceIssuanceSortableValue = (row, key) => {
    switch (key) {
        case 'giReqNo':
            return Number(row.gi_request_id) || 0;
        case 'giReqLineId':
            return Number(row.gi_request_line_id) || 0;
        case 'customer':
            return row.vendor_name || '';
        case 'subOrderId':
            return Number(row.sub_order_id) || 0;
        case 'buyer':
            return formatBuyerColumnDisplay(row.buyer_name, row.buyer_detail);
        case 'style':
            return row.style_no || '';
        case 'file':
            return row.file_no ?? '';
        case 'delivery':
            return row.delivery_name || '';
        case 'manager':
            return row.manager_name || '';
        case 'item':
            return row.thread_name || '';
        case 'size':
            return row.size_name || '';
        case 'color':
            return row.color || '';
        case 'orderQty':
            return Number(row.order_qty) || 0;
        case 'price':
            return Number(row.price) || 0;
        case 'currency':
            return row.price_currency || '';
        case 'giReq': {
            const ts = row.gi_request_planned_dispatch_at
                ? dayjs(row.gi_request_planned_dispatch_at).valueOf()
                : 0;
            const q = Number(row.gi_request_line_qty) || 0;
            return ts * 1e6 + q;
        }
        case 'packing': {
            const pl = row.packing_lot_lines;
            if (Array.isArray(pl) && pl.length > 0) {
                const first = pl[0];
                const ts = first?.expected_gi_at ? dayjs(first.expected_gi_at).valueOf() : 0;
                const q = Number(first?.qty) || 0;
                return ts * 1e9 + q * 1e3 + pl.length;
            }
            return row.packing || '';
        }
        case 'giPost':
            return row.gi_post || '';
        case 'priceSum':
            return priceSumForInvoiceSort(row);
        case 'invoiceNo': {
            const invLines = getDedupedSortedInvoiceLines(row.invoice_lines || []);
            if (invLines.length > 0) {
                return invLines
                    .map(
                        (ln) =>
                            `${ln?.invoice_no ?? ''}\t${getInvoiceLineDisplayQuantity(ln) ?? ''}`
                    )
                    .join('\n');
            }
            return row.invoice_no || row.existing_invoice_no || '';
        }
        default:
            return row[key] ?? '';
    }
};

/**
 * @param {Array} rows
 * @param {{ key: string|null, direction: 'asc'|'desc'|null }} sortConfig
 */
export const sortInvoiceIssuanceRows = (rows, sortConfig) => {
    if (!Array.isArray(rows)) return rows;
    // 컬럼 정렬 미선택 시 API 응답 순서 유지 (서버 customer 정렬 + 무한 스크롤 append)
    if (!sortConfig?.key || !sortConfig?.direction) {
        return rows;
    }
    const sorted = [...rows];
    sorted.sort((a, b) => {
        const aValue = getInvoiceIssuanceSortableValue(a, sortConfig.key);
        const bValue = getInvoiceIssuanceSortableValue(b, sortConfig.key);

        if (aValue === bValue) {
            return String(a.id || '').localeCompare(String(b.id || ''));
        }
        if (aValue === null || aValue === undefined) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (bValue === null || bValue === undefined) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const compared = String(aValue).localeCompare(String(bValue));
        return sortConfig.direction === 'asc' ? compared : -compared;
    });

    return sorted;
};

const buildLookupMaps = (cacheData = {}) => {
    const vendors = cacheData.vendors || [];
    const buyers = cacheData.buyers || [];
    const managers = cacheData.managers || [];
    const deliveries = cacheData.deliveries || [];
    const items = cacheData.items || [];

    const vendorMap = new Map();
    vendors.forEach((vendor) => {
        const displayName = vendor.partner_name || vendor.vendor_name || vendor.company_name || '';
        [vendor.vendor_id, vendor.partner_id, vendor.company_id].forEach((id) => {
            if (id !== undefined && id !== null && !vendorMap.has(String(id))) {
                vendorMap.set(String(id), displayName);
            }
        });
    });

    const buyerMap = new Map();
    buyers.forEach((buyer) => {
        const displayName = buyer.partner_name || buyer.buyer_name || buyer.partner_group || buyer.name || '';
        [buyer.partner_id, buyer.buyer_id].forEach((id) => {
            if (id !== undefined && id !== null && !buyerMap.has(String(id))) {
                buyerMap.set(String(id), displayName);
            }
        });
    });

    const managerMap = new Map();
    managers.forEach((manager) => {
        const displayName = manager.manager_name || manager.name || '';
        [manager.manager_id, manager.id].forEach((id) => {
            if (id !== undefined && id !== null && !managerMap.has(String(id))) {
                managerMap.set(String(id), displayName);
            }
        });
    });

    const deliveryMap = new Map();
    deliveries.forEach((delivery) => {
        const displayName = delivery.delivery_name || delivery.name || '';
        if (delivery.delivery_id !== undefined && delivery.delivery_id !== null) {
            deliveryMap.set(String(delivery.delivery_id), displayName);
        }
    });

    const itemMap = new Map();
    items.forEach((item) => {
        const itemName = item.name || item.item_name || '';
        const itemSpec = item.specification || item.size_name || '';
        [item.item_id, item.thread_id, item.id].forEach((id) => {
            if (id !== undefined && id !== null && !itemMap.has(String(id))) {
                itemMap.set(String(id), {
                    itemName,
                    itemSpec
                });
            }
        });
    });

    return {
        vendorMap,
        buyerMap,
        managerMap,
        deliveryMap,
        itemMap
    };
};

const normalizePackingLotLines = (raw) => {
    const source = Array.isArray(raw?.packing_lots)
        ? raw.packing_lots
        : Array.isArray(raw?.packing_lot_lines)
          ? raw.packing_lot_lines
          : [];
    return source
        .filter((line) => line && typeof line === 'object')
        .map((line) => ({
            lot_id: line.lot_id ?? null,
            expected_gi_at: line.expected_gi_at ?? null,
            qty: line.qty
        }));
};

const normalizeGiPostLines = (raw) => {
    const source = Array.isArray(raw?.gi_posts)
        ? raw.gi_posts
        : Array.isArray(raw?.gi_post_lines)
          ? raw.gi_post_lines
          : [];
    return source
        .filter((line) => line && typeof line === 'object')
        .map((line) => ({
            gi_post_id: line.gi_post_id,
            gi_post_no: line.gi_post_no,
            outbound_lot_no: line.outbound_lot_no,
            gi_time: line.gi_time,
            to_delivery_id: line.to_delivery_id,
            remarks: line.remarks,
            line_no: line.line_no,
            item_id: line.item_id,
            qty: line.qty,
            uom_id: line.uom_id,
            lot_id: line.lot_id,
            sub_order_id: line.sub_order_id,
            unit_price: line.unit_price,
            currency: line.currency
        }));
};

const normalizeInvoiceLines = (raw) => {
    const source = Array.isArray(raw?.invoice_lines) ? raw.invoice_lines : [];
    return source
        .filter((ln) => ln && typeof ln === 'object')
        .map((ln) => {
            const quantity = ln.quantity ?? ln.qty ?? null;
            return {
                invoice_id: ln.invoice_id ?? null,
                invoice_no: ln.invoice_no != null ? String(ln.invoice_no) : '',
                quantity,
                qty: quantity,
                gi_request_line_id: ln.gi_request_line_id ?? null,
                gi_request_id: ln.gi_request_id ?? null,
                invoice_date: ln.invoice_date ?? ln.created_at ?? null
            };
        });
};

/** issuance_list flat item → 테이블 행. Bundle로 이름만 부착. */
export const processInvoiceData = (data, cacheData = {}) => {
    const { vendorMap, buyerMap, managerMap, deliveryMap, itemMap } = buildLookupMaps(cacheData);

    return (data || []).map((raw) => {
        if (!raw || typeof raw !== 'object') return null;

        const main_order_id = raw.main_order_id;
        const sub_order_id = raw.sub_order_id;
        const vendor_id = raw.vendor_id;
        const buyer_id = raw.buyer_id;
        const delivery_id = raw.delivery_id;
        const manager_id = raw.manager_id;
        const item_id = raw.item_id;
        const order_qty = raw.order_qty ?? raw.qty ?? raw.qty_requested ?? 0;

        const gi_request_id = raw.gi_request_id ?? null;
        const gi_request_line_id = raw.gi_request_line_id ?? raw.gi_line_id ?? null;
        const gi_request_line_qty =
            raw.gi_request_line_qty != null
                ? raw.gi_request_line_qty
                : raw.qty_delivered ?? raw.qty_requested ?? order_qty ?? 0;

        const rowId =
            gi_request_id != null && gi_request_line_id != null
                ? `${gi_request_id}-${gi_request_line_id}`
                : gi_request_line_id != null
                  ? String(gi_request_line_id)
                  : `${main_order_id}-${sub_order_id}`;

        const itemNameFromCache = itemMap.get(String(item_id))?.itemName || '';
        const itemSpecFromCache = itemMap.get(String(item_id))?.itemSpec || '';
        const bracketMatch = itemNameFromCache.match(SIZE_BRACKET_REGEX);
        const thread_name = bracketMatch ? bracketMatch[1].trim() : itemNameFromCache;
        const size_from_item = bracketMatch ? bracketMatch[2].trim() : itemSpecFromCache;

        const packing_lot_lines = normalizePackingLotLines(raw);
        const gi_post_lines = normalizeGiPostLines(raw);
        const invoice_lines = normalizeInvoiceLines(raw);
        const gi_post_qty_total = gi_post_lines.reduce((sum, line) => {
            const q = Number(line?.qty);
            return sum + (Number.isFinite(q) ? q : 0);
        }, 0);

        const dyeing_no_short =
            raw.dyeing_no_short != null && String(raw.dyeing_no_short).trim() !== ''
                ? String(raw.dyeing_no_short).trim()
                : '';
        const dyeing_no_full = raw.dyeing_no ?? raw.dyeing_no_full ?? null;
        const dyeing_no =
            dyeing_no_short ||
            (dyeing_no_full != null && String(dyeing_no_full).trim() !== ''
                ? String(dyeing_no_full)
                : '');

        const originalData = {
            ...raw,
            vendor_id,
            manager_id,
            buyer_id,
            delivery_id,
            item_id
        };

        return {
            id: rowId,
            gi_request_id,
            gi_request_line_id,
            gi_request_line_qty,
            gi_request_planned_dispatch_at: raw.gi_request_planned_dispatch_at ?? null,
            order_qty: order_qty ?? 0,
            main_order_id,
            sub_order_id,
            created_by: raw.created_by ?? '',
            order_received_date: raw.order_created_at,
            vendor_id,
            vendor_name: vendorMap.get(String(vendor_id)) || '',
            buyer_id,
            buyer_name: buyerMap.get(String(buyer_id)) || '',
            buyer_detail: raw.buyer_detail != null ? String(raw.buyer_detail).trim() : '',
            manager_id,
            manager_name: managerMap.get(String(manager_id)) || '',
            item_id,
            thread_name,
            size_name: size_from_item,
            color: raw.color,
            file_no: raw.file_no,
            style_no: raw.style_no,
            qty_requested: gi_request_line_qty,
            qty_delivered: raw.qty_delivered ?? gi_request_line_qty,
            price: raw.price,
            price_currency: raw.price_currency,
            amount: (Number(gi_request_line_qty) || 0) * (Number(raw.price) || 0),
            existing_invoice_no: raw.existing_invoice_no,
            existing_invoice_status: raw.existing_invoice_status,
            time_of_arrival: raw.time_of_arrival,
            originalData,
            delivery_id,
            delivery_name: deliveryMap.get(String(delivery_id)) || '',
            dyeing_no,
            dyeing_no_short,
            dyeing_no_full,
            packing_lot_lines,
            gi_post_lines,
            gi_post_qty_total,
            invoice_lines,
            packing: raw.packing ?? '',
            gi_post: raw.gi_post ?? '',
            invoice_no: raw.invoice_no ?? raw.existing_invoice_no ?? '',
            row_status: raw.status ?? raw.invoice_status ?? ''
        };
    }).filter(Boolean);
};

/**
 * issuance_list / summary 공통 쿼리.
 * @param {object} searchParams UI 검색 상태
 * @param {object} [options]
 * @param {number} [options.limit]
 * @param {number} [options.offset]
 * @param {string|null} [options.createdBy] 내 오더 — user.user_id
 * @param {boolean} [options.unissuedOnly]
 * @param {boolean} [options.includePagination=true] summary는 false
 */
export const buildSearchParams = (searchParams, options = {}) => {
    const {
        limit = ISSUANCE_PAGE_LIMIT,
        offset = 0,
        createdBy = null,
        unissuedOnly = false,
        includePagination = true,
        dateBasis = searchParams?.dateBasis || 'planned_dispatch_at'
    } = options;

    const params = {
        date_basis: dateBasis || 'planned_dispatch_at'
    };

    if (searchParams?.startDate) {
        const start = dayjs(searchParams.startDate);
        if (start.isValid()) {
            params.start_datetime = start.startOf('day').utc().format();
        }
    }
    if (searchParams?.endDate) {
        const end = dayjs(searchParams.endDate);
        if (end.isValid()) {
            params.end_datetime = end.add(1, 'day').startOf('day').utc().format();
        }
    }

    const vendorId = toNumericId(searchParams?.vendorId ?? searchParams?.vendorName);
    if (vendorId != null) params.vendor_id = vendorId;

    const managerId = toNumericId(searchParams?.managerId ?? searchParams?.managerName);
    if (managerId != null) params.manager_id = managerId;

    const itemId = toNumericId(searchParams?.itemId ?? searchParams?.threadName);
    if (itemId != null) params.item_id = itemId;

    const deliveryId = toNumericId(searchParams?.deliveryId);
    if (deliveryId != null) params.delivery_id = deliveryId;

    const subOrderRaw =
        searchParams?.subOrderId != null ? String(searchParams.subOrderId).trim() : '';
    const subOrderId = toNumericId(subOrderRaw);
    if (subOrderId != null && /^\d+$/.test(subOrderRaw)) {
        params.sub_order_id = subOrderId;
    }

    const dyeingNoRaw =
        searchParams?.dyeNo != null ? String(searchParams.dyeNo).trim() : '';
    const dyeingNo = toNumericId(dyeingNoRaw);
    if (dyeingNo != null && /^\d+$/.test(dyeingNoRaw)) {
        params.dyeing_no = dyeingNo;
    }

    if (createdBy) {
        params.created_by = createdBy;
    }
    if (unissuedOnly) {
        params.unissued_only = true;
    }

    if (includePagination) {
        params.limit = Math.max(1, Number(limit) || ISSUANCE_PAGE_LIMIT);
        params.offset = Math.max(0, Number(offset) || 0);
    }

    return params;
};

/** summary용 — limit/offset 제외 */
export const buildSummaryParams = (searchParams, options = {}) =>
    buildSearchParams(searchParams, { ...options, includePagination: false });

/** 한 번의 발급에 단일 POST; `main_order_id`는 선택 행에서 모은 고유 id 배열. */
export const createInvoiceGroups = (selectedData, vatOption, deliveryDateByRowKey = {}) => {
    const round2 = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return 0;
        return Math.round((n + Number.EPSILON) * 100) / 100;
    };
    if (!selectedData?.length) {
        return {};
    }

    const first = selectedData[0];
    const mainOrderIds = [...new Set(selectedData.map((item) => item.main_order_id))];
    const findFirstNonNull = (field) => {
        for (const it of selectedData) {
            const v = it?.originalData?.[field];
            if (v != null && v !== '') return v;
        }
        return null;
    };
    const customerId = first.originalData?.vendor_id ?? findFirstNonNull('vendor_id');
    const managerId = first.originalData?.manager_id ?? findFirstNonNull('manager_id');

    const group = {
        main_order_id: mainOrderIds,
        customer_id: customerId,
        manager_id: managerId,
        invoice_date: dayjs().format('YYYY-MM-DD'),
        due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        currency: first.price_currency,
        amount: 0,
        vat_amount: 0,
        vat_amount_2: 0,
        items: []
    };

    const getHistoryQtySum = (it) => {
        if (!it || !Array.isArray(it.gi_history) || it.gi_history.length === 0) return 0;
        return it.gi_history.reduce((sum, h) => sum + (parseFloat(h?.qty) || 0), 0);
    };

    selectedData.forEach((item) => {
        const historySum = getHistoryQtySum(item);
        const effectiveQty = historySum > 0
            ? historySum
            : Number(item.gi_request_line_qty ?? item.qty_delivered ?? item.qty_requested ?? item.originalData?.qty ?? 0) || 0;
        const itemAmount = effectiveQty * item.price;
        group.amount = round2(group.amount + itemAmount);

        let vatRate = 0;
        if (vatOption === 'BC21' || vatOption === 'BC45') {
            vatRate = 0.11;
        }

        if (vatOption === 'BC21') {
            group.vat_amount = round2(group.vat_amount + itemAmount * vatRate);
        } else if (vatOption === 'BC45') {
            group.vat_amount_2 = round2(group.vat_amount_2 + itemAmount * vatRate);
        }

        const rowKey = item.id || `${item.main_order_id}-${item.sub_order_id}`;
        const explicitDeliveryDate = deliveryDateByRowKey[String(rowKey)];
        const giReqParsed = parseDeliveryDateForUi(item.gi_request_planned_dispatch_at);
        const giReqDate = giReqParsed ? giReqParsed.format('YYYY-MM-DD') : null;
        const latestDateFromLines = (lines, field) => {
            if (!Array.isArray(lines) || lines.length === 0) return null;
            const sorted = lines
                .map((l) => parseDeliveryDateForUi(l?.[field]))
                .filter((d) => d != null)
                .sort((a, b) => b.valueOf() - a.valueOf());
            return sorted.length > 0 ? sorted[0].format('YYYY-MM-DD') : null;
        };
        const packingDate = latestDateFromLines(item.packing_lot_lines, 'expected_gi_at');
        const giPostDate = latestDateFromLines(item.gi_post_lines, 'gi_time');
        const deliveryDate = explicitDeliveryDate || giReqDate || packingDate || giPostDate;
        const remarkParts = [];
        if (item.gi_request_id != null && item.gi_request_id !== '') {
            remarkParts.push(`GI_REQ:${item.gi_request_id}`);
        }
        if (item.gi_request_line_id != null && item.gi_request_line_id !== '') {
            remarkParts.push(`GI_LINE:${item.gi_request_line_id}`);
        }
        const remarkValue = remarkParts.length > 0 ? remarkParts.join('|') : null;

        group.items.push({
            sub_order_id: item.sub_order_id,
            ...(item.gi_request_id != null ? { gi_request_id: item.gi_request_id } : {}),
            ...(item.gi_request_line_id != null ? { gi_request_line_id: item.gi_request_line_id } : {}),
            quantity: effectiveQty,
            unit_price: item.price,
            amount: round2(itemAmount),
            ...(remarkValue ? { remark: remarkValue } : {}),
            ...(deliveryDate ? { delivery_date: deliveryDate } : {})
        });
    });

    return { combined: group };
};

export const validateInvoiceSelection = (selectedData, t) => {
    // 1. 거래처명 일치 여부 확인
    const uniqueVendors = [...new Set(selectedData.map(item => item.vendor_name))];
    if (uniqueVendors.length > 1) {
        return {
            isValid: false,
            alertInfo: {
                open: true,
                title: t('invoice.warning'),
                message: t('invoice.differentVendors'),
                severity: 'error'
            }
        };
    }

    // 2. 통화 종류 일치 여부 확인
    const uniqueCurrencies = [...new Set(selectedData.map(item => item.price_currency))];
    if (uniqueCurrencies.length > 1) {
        return {
            isValid: false,
            alertInfo: {
                open: true,
                title: t('invoice.warning'),
                message: t('invoice.differentCurrencies'),
                severity: 'error'
            }
        };
    }

    // 3. 기존 인보이스 발행 여부 확인
    const itemsWithExistingInvoice = selectedData.filter(item => 
        item.existing_invoice_no && item.existing_invoice_no !== null
    );

    if (itemsWithExistingInvoice.length > 0) {
        const existingInvoiceCount = itemsWithExistingInvoice.length;
        const totalCount = selectedData.length;
        
        return {
            isValid: false,
            hasExistingInvoice: true,
            alertInfo: {
                open: true,
                title: t('invoice.warning'),
                message: t('invoice.existingInvoiceWarning', { 
                    existingCount: existingInvoiceCount, 
                    totalCount: totalCount 
                }),
                severity: 'warning',
                isConfirm: false
            }
        };
    }

    return { isValid: true };
}; 